import { useEffect, useMemo, useState } from 'react';
import Transform from '../../components/TransForm/Transform';
import StyledTable from '../../components/table-component/StyledTable';
import { useDispatch, useSelector } from 'react-redux';
import { createAgence, deleteAgence, fetchAgences, updateAgence } from '../../data/slices/agenceSlice';
import Agence from '../../data/schemas/Agence';
import { notify } from '../../settings/toastSetting';
import { Dialog, Box, Paper, Typography } from '@mui/material';
import { Add, Business } from '@mui/icons-material';
import { output, sortByProp, COLORS } from '../../settings/themeSettings';
import PageHeader from '../../components/layout/PageHeader';
import useIsAdmin from '../../hooks/useIsAdmin';

const AgencePage = (props) => {
    const { agences } = useSelector((state) => state.agences);
    const dispatch = useDispatch();
    const isAdmin = useIsAdmin();
    const [agence, setAgence] = useState(null);
    const [open, setOpen] = useState(false);
    const emptyAgence = useMemo(() => new Agence(), []);

    const handleClose = () => {
        setOpen(false);
        setAgence(null);
    }

    const addAgence = (data, clearForm) => {
        handleClose();
        if (data.id) {
            updateAgence(data, (err) => output(err), (resp) => {
                clearForm();
                notify("L'agence a été enregistrée avec succès !");
                dispatch(fetchAgences());
            });
            return;
        }

        createAgence(data, (err) => output(err), (resp) => {
            clearForm();
            notify("L'agence a été ajoutée avec succès !");
            dispatch(fetchAgences());
        });
    }

    const onAgenceSelected = (id) => {
        setAgence(agences.find((a) => a.id === id));
        setOpen(true);
    }

    const onDelete = (id) => {
        handleClose();
        deleteAgence(id, (err) => output(err), (resp) => {
            setAgence(null);
            notify("L'agence a bien été supprimée !");
            dispatch(fetchAgences());
        })
    }

    useEffect(() => {
        dispatch(fetchAgences());
    }, [dispatch]);

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <PageHeader 
                title="Gestion des Agences"
                subtitle="Gérez vos agences partenaires et leurs points de vente"
                icon={Business}
                breadcrumbs={[
                    { label: 'Paramètres', path: '/parametres' },
                    { label: 'Agences', path: '/parametres/agences' }
                ]}
                actions={isAdmin ? [
                    { 
                        label: 'Ajouter une agence', 
                        icon: <Add />, 
                        onClick: () => { setAgence(null); setOpen(true); },
                        color: 'primary'
                    }
                ] : []}
            />

            <Paper elevation={0} sx={{ 
                borderRadius: '16px', 
                overflow: 'hidden', 
                border: '1px solid',
                borderColor: COLORS.gray[200],
                bgcolor: 'white'
            }}>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: COLORS.gray[100], bgcolor: COLORS.gray[50] }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.secondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Liste des agences ({agences.length})
                    </Typography>
                </Box>
                
                <StyledTable 
                    rows={[...agences].sort((a,b) => sortByProp(a, b, 'nom'))} 
                    head={Object.keys(new Agence())} 
                    onOptionClicked={onAgenceSelected}
                />
            </Paper>

            <Dialog 
                open={open} 
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '20px', p: 1 }
                }}
            >
                <Transform 
                    onSubmit={addAgence} 
                    mandatories={["nom", "adresse"]} 
                    entity={emptyAgence} 
                    toUpdate={agence} 
                    onDelete={isAdmin ? onDelete : undefined}
                    onCancel={handleClose}
                />
            </Dialog>
        </Box>
    );
}

export default AgencePage;
